import { useCallback, useMemo } from 'react';

/**
 * Shared helpers for mutating checkpoint geometry while keeping the existing object intact.
 * Components call commitGeometry with partial updates; the hook merges them with the current geometry.
 * @param {{ cp: any, updateCheckpoint: function, defaultType?: string }} params
 */
export function useGeometryEditing({ cp, updateCheckpoint, defaultType = 'circle' }) {
  const geometry = useMemo(() => cp?.geometry || {}, [cp?.geometry]);

  const commitGeometry = useCallback(
    (partial) => {
      if (!cp?.id) return;
      const type = partial?.type || geometry.type || defaultType;
      updateCheckpoint(cp.id, {
        geometry: { ...geometry, ...partial, type },
      });
    },
    [cp?.id, defaultType, geometry, updateCheckpoint]
  );

  const geometryPoints = useMemo(
    () =>
      Array.isArray(geometry.coord)
        ? geometry.coord
        : Array.isArray(geometry.points)
        ? geometry.points
        : [],
    [geometry.coord, geometry.points]
  );

  const setPoints = useCallback(
    (points) => commitGeometry({ coord: points, points }),
    [commitGeometry]
  );

  const addPoint = useCallback(
    (point) => {
      const next = Array.isArray(geometryPoints) ? geometryPoints.slice() : [];
      next.push(point);
      commitGeometry({ coord: next, points: next });
    },
    [commitGeometry, geometryPoints]
  );

  return {
    geometry,
    commitGeometry,
    setPoints,
    addPoint,
  };
}

export default useGeometryEditing;
